import {
  RATE_CARDS_DEFAULT_SORT,
  RATE_CARDS_LINES_PAGE_SIZE,
  RATE_CARDS_LIST_PAGE_SIZE,
  RATE_CARDS_SORT_COLUMNS,
  RATE_CARD_LINE_FILTER_KEYS,
} from './constants';
import type { RateCardSideId } from './constants';

/**
 * `asc` / `desc`, from whichever spelling the grid used: the tables keep antd's `ascend` / `descend`
 * in the URL because the header arrow reads it back, while the endpoints document the short pair.
 */
export const toSortOrder = (value: unknown): 'asc' | 'desc' | undefined => {
  if (typeof value !== 'string') return undefined;

  if (value.startsWith('desc')) return 'desc';

  if (value.startsWith('asc')) return 'asc';

  return undefined;
};

/** A filter value as the grid left it — one id, several, or nothing — as a plain array. */
export const toFilterValues = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter(item => item !== null && item !== '').map(String);

  if (value === null || value === undefined || value === '') return [];

  return [String(value)];
};

const toPositiveInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/** antd's spelling of each order: the header arrow reads the sort back from the URL and knows no other. */
const GRID_ORDERS = { asc: 'ascend', desc: 'descend' } as const;

interface IListSort {
  column: string;
  order: (typeof GRID_ORDERS)[keyof typeof GRID_ORDERS];
}

/**
 * The sort a list query carries, in the grid's spelling, or the default when it carries none. An `order` with no
 * `column`, a `column` the grid cannot sort, or an order it cannot read is malformed, not a request.
 */
export const resolveListSort = (query: Record<string, unknown>): IListSort => {
  const { column, order } = query;
  const sortOrder = toSortOrder(order);

  return typeof column === 'string' && (RATE_CARDS_SORT_COLUMNS as readonly string[]).includes(column) && sortOrder
    ? { column, order: GRID_ORDERS[sortOrder] }
    : RATE_CARDS_DEFAULT_SORT;
};

/**
 * The rate cards list request. Only documented parameters are forwarded — the grid also parks `tab` and its own
 * bookkeeping in the URL. `filter` is the search term, matched against `name`; no usable sort means the default.
 */
export const buildListSearch = (query: Record<string, unknown> = {}): Record<string, unknown> => {
  const sort = resolveListSort(query);
  const status = toFilterValues(query.status);
  // `onChange` hands back `filter: null` whenever anything but the search changes. The type test earns
  // its keep: `?filter=a&filter=b` parses to an array, which is truthy and breaks the backend's `.split`.
  const filter = typeof query.filter === 'string' ? query.filter : '';

  return {
    page: toPositiveInt(query.page, 1),
    pageSize: toPositiveInt(query.pageSize, RATE_CARDS_LIST_PAGE_SIZE),
    column: sort.column,
    // Never undefined: `resolveListSort` only keeps an order `toSortOrder` can read.
    order: toSortOrder(sort.order),
    ...(status.length ? { status } : {}),
    ...(filter ? { filter } : {}),
  };
};

/**
 * Identifies a lines query. The reducer keeps loaded rows across a refetch of the same query, and
 * drops them when it changed — the old side's or page's rows under the new one are the wrong answer.
 */
export const linesRequestKey = (input: ILinesSearchInput): string =>
  JSON.stringify([
    input.side,
    input.page ?? 1,
    input.pageSize ?? RATE_CARDS_LINES_PAGE_SIZE,
    input.column ?? '',
    toSortOrder(input.order) ?? '',
    RATE_CARD_LINE_FILTER_KEYS.map(key => toFilterValues(input.filters?.[key])),
  ]);

export interface ILinesSearchInput {
  side: RateCardSideId;
  page?: number;
  pageSize?: number;
  column?: string;
  order?: string;
  filters?: Record<string, unknown>;
}

/**
 * The lines request. `side` is a path segment on the endpoint, not a query parameter, so nothing the
 * filter payload carries for it is forwarded.
 */
export const buildLinesSearch = ({
  page,
  pageSize,
  column,
  order,
  filters = {},
}: ILinesSearchInput): Record<string, unknown> => {
  const sortOrder = toSortOrder(order);

  const selected = RATE_CARD_LINE_FILTER_KEYS.reduce<Record<string, string[]>>((acc, key) => {
    const values = toFilterValues(filters[key]);

    return values.length ? { ...acc, [key]: values } : acc;
  }, {});

  return {
    page: toPositiveInt(page, 1),
    pageSize: toPositiveInt(pageSize, RATE_CARDS_LINES_PAGE_SIZE),
    ...(column ? { column } : {}),
    ...(sortOrder ? { order: sortOrder } : {}),
    ...selected,
  };
};
